import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '@/components/layout/Header';
import { EventCard } from '@/components/schedule/EventCard';
import { useInitWedding, useWedding } from '@/hooks/useWedding';
import { useEvents, useBulkCreateEvents } from '@/hooks/useEvents';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import type { ParsedEvent, EventCategory } from '@/lib/types';
import { track } from '@/lib/analytics';

function getNextSaturday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = (6 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function combineDatetime(date: string, isoTime: string): string {
  if (!isoTime) return `${date}T00:00:00`;
  // Replace date portion of ISO string
  if (isoTime.length >= 10) {
    return `${date}${isoTime.slice(10)}`;
  }
  return `${date}T00:00:00`;
}

export function ImportReviewPage() {
  const [, navigate] = useLocation();
  const { weddingId } = useInitWedding();
  const { data: wedding } = useWedding(weddingId);
  const { data: existingEvents } = useEvents(weddingId);
  const bulkCreate = useBulkCreateEvents(weddingId ?? '');
  const { toast } = useToast();

  const [editedEvents, setEditedEvents] = useState<ParsedEvent[]>([]);
  const [weddingDate, setWeddingDate] = useState<string>('');
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Read from sessionStorage on mount
  useEffect(() => {
    const raw = sessionStorage.getItem('wedtrack:parsed');
    if (!raw) {
      navigate('/schedule/import/paste');
      return;
    }
    try {
      const parsed = JSON.parse(raw) as ParsedEvent[];
      setEditedEvents(parsed);
    } catch {
      navigate('/schedule/import/paste');
    }
  }, [navigate]);

  // Set wedding date once wedding loads
  useEffect(() => {
    if (!weddingDate) {
      setWeddingDate(wedding?.weddingDate ?? getNextSaturday());
    }
  }, [wedding, weddingDate]);

  function handleChange(index: number, updated: ParsedEvent) {
    setEditedEvents((prev) => prev.map((e, i) => (i === index ? updated : e)));
  }

  function handleDelete(index: number) {
    setEditedEvents((prev) => prev.filter((_, i) => i !== index));
  }

  function handleConfirmClick() {
    if ((existingEvents?.length ?? 0) > 0) {
      setShowDuplicateDialog(true);
    } else {
      void doImport('append');
    }
  }

  async function doImport(action: 'replace' | 'append') {
    if (!weddingId) return;
    setIsImporting(true);
    setShowDuplicateDialog(false);

    try {
      if (action === 'replace' && existingEvents && existingEvents.length > 0) {
        // Delete each existing event
        await Promise.all(
          existingEvents.map((ev) =>
            api.delete<{ deleted: boolean }>(`/api/weddings/${weddingId}/events/${ev.id}`)
          )
        );
      }

      const eventsToCreate = editedEvents.map((event, index) => ({
        name: event.name,
        startTime: combineDatetime(weddingDate, event.startTime),
        endTime: combineDatetime(weddingDate, event.endTime),
        category: event.category as EventCategory,
        location: event.location ?? null,
        status: 'upcoming' as const,
        notify: true,
        sortOrder: index,
        notes: null,
      }));

      await bulkCreate.mutateAsync(eventsToCreate);

      track('import_confirmed', { event_count: editedEvents.length });
      sessionStorage.removeItem('wedtrack:parsed');
      navigate('/schedule');
      toast(`${editedEvents.length} events imported`, 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import events';
      toast(message, 'error');
    } finally {
      setIsImporting(false);
    }
  }

  function handleDiscard() {
    sessionStorage.removeItem('wedtrack:parsed');
    navigate('/schedule');
  }

  const hasLowConfidence = editedEvents.some((e) => e.confidence < 0.7);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'var(--color-background)',
      }}
    >
      <Header title="Review Timeline" backHref="/schedule/import/paste" />

      {/* Content */}
      <div style={{ flex: 1, padding: '16px 16px 140px' }}>
        {/* Wedding date picker */}
        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              fontSize: 13,
              color: 'var(--color-text-secondary)',
            }}
          >
            Wedding Date
            <input
              type="date"
              value={weddingDate}
              onChange={(e) => setWeddingDate(e.target.value)}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: '8px 12px',
                color: 'var(--color-text)',
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
                width: '100%',
              }}
            />
          </label>
        </div>

        {/* Low confidence warning bar */}
        {hasLowConfidence && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              background: 'rgba(251,191,36,0.08)',
              border: '1px solid rgba(251,191,36,0.2)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              color: '#FBBF24',
              lineHeight: 1.5,
            }}
          >
            Some categories were guessed — review highlighted cards below
          </div>
        )}

        {/* Event list */}
        {editedEvents.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 16px',
              color: 'var(--color-text-secondary)',
            }}
          >
            <p style={{ fontSize: 15, marginBottom: 12 }}>No events left</p>
            <button
              onClick={() => navigate('/schedule/import/paste')}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-primary)',
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                textDecoration: 'underline',
              }}
            >
              Go back and paste a timeline
            </button>
          </div>
        ) : (
          <div>
            {editedEvents.map((event, index) => (
              <EventCard
                key={index}
                event={event}
                index={index}
                onChange={handleChange}
                onDelete={handleDelete}
                weddingDate={weddingDate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom fixed bar */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 512,
          padding: '12px 16px max(16px, env(safe-area-inset-bottom))',
          background: 'linear-gradient(to top, var(--color-background) 70%, transparent)',
          zIndex: 100,
        }}
      >
        <button
          onClick={handleConfirmClick}
          disabled={editedEvents.length === 0 || isImporting || !wedding}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 'var(--radius-lg)',
            border: 'none',
            background:
              editedEvents.length === 0 || isImporting || !wedding
                ? 'rgba(212,168,75,0.3)'
                : 'var(--color-primary)',
            color:
              editedEvents.length === 0 || isImporting || !wedding
                ? 'rgba(212,168,75,0.5)'
                : '#1A1A2E',
            fontSize: 15,
            fontWeight: 600,
            cursor: editedEvents.length === 0 || isImporting || !wedding ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-body)',
            transition: 'background 150ms ease',
          }}
        >
          {isImporting
            ? 'Importing…'
            : !wedding
            ? 'Setting up…'
            : `Confirm Import ${editedEvents.length} Event${editedEvents.length === 1 ? '' : 's'}`}
        </button>

        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button
            onClick={handleDiscard}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
            }}
          >
            Discard
          </button>
        </div>
      </div>

      {/* Duplicate dialog */}
      {showDuplicateDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 300,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          {/* Backdrop */}
          <div
            onClick={() => setShowDuplicateDialog(false)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
          />

          {/* Sheet */}
          <div
            className="animate-slide-up"
            style={{
              position: 'relative',
              background: 'var(--color-surface)',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: '24px 16px max(24px, env(safe-area-inset-bottom))',
              zIndex: 1,
            }}
          >
            {/* Drag handle */}
            <div
              style={{
                width: 40,
                height: 4,
                background: 'rgba(148,163,184,0.3)',
                borderRadius: 2,
                margin: '0 auto 20px',
              }}
            />

            <h2
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: 'var(--color-text)',
                margin: '0 0 6px',
                textAlign: 'center',
              }}
            >
              Import {editedEvents.length} Event{editedEvents.length === 1 ? '' : 's'}
            </h2>
            <p
              style={{
                fontSize: 13,
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
                margin: '0 0 24px',
                lineHeight: 1.5,
              }}
            >
              You already have {existingEvents?.length ?? 0} event
              {(existingEvents?.length ?? 0) === 1 ? '' : 's'}. What would you like to do?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Replace All */}
              <button
                onClick={() => {
                  void doImport('replace');
                }}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(248,113,113,0.3)',
                  background: 'rgba(248,113,113,0.12)',
                  color: 'var(--color-error)',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  transition: 'background 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'rgba(248,113,113,0.22)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'rgba(248,113,113,0.12)';
                }}
              >
                Replace All
              </button>

              {/* Add to Existing */}
              <button
                onClick={() => {
                  void doImport('append');
                }}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  background: 'var(--color-primary)',
                  color: '#1A1A2E',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  transition: 'background 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-primary-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-primary)';
                }}
              >
                Add to Existing
              </button>

              {/* Cancel */}
              <button
                onClick={() => {
                  setShowDuplicateDialog(false);
                }}
                style={{
                  width: '100%',
                  height: 44,
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--color-border)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  transition: 'background 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Importing overlay */}
      {isImporting && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <div
            className="animate-spin"
            style={{
              width: 40,
              height: 40,
              border: '3px solid rgba(212,168,75,0.2)',
              borderTopColor: 'var(--color-primary)',
              borderRadius: '50%',
            }}
          />
          <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: 0 }}>
            Importing events…
          </p>
        </div>
      )}
    </div>
  );
}
